import type { DemoDoc } from '@mado/schema';
import { luminance } from '@mado/schema/color';
import { CHAPTER_MS } from '@mado/schema/timeline';
import { bestUrl } from './media';
import { el } from './stage';
import { stringsFor, type Strings } from './strings';

/**
 * Everything around the demo: progress, the badge, the CTA, the end card, the
 * creator's logo, and the intro / chapter cards.
 *
 * The badge is the compounding distribution asset, not decoration — see
 * DISTRIBUTION-PLAN §6. It is a designed funnel: it lands on a page that replays
 * the demo the viewer just watched, with the upload dialog already open, and it
 * carries attribution so badge → click → signup is measurable as its own channel.
 */

const BADGE_HOME = 'https://mado.show';

/** The mado mark at badge size (16px, the guidelines' minimum): olive, gold
 *  and rust windows with the cream cross, the gaps masked so it reads on any
 *  surface — the same geometry as `Brand/mado/svg/mado-mark.svg`. */
function markSvg(): string {
  const cut = (x: number, y: number) =>
    `<rect x="${x}" y="${y}" width="75" height="75" rx="19.82"/>`;
  return (
    `<svg width="16" height="16" viewBox="0 0 100 100" aria-hidden="true" focusable="false"><defs>` +
    `<mask id="mdw0" maskUnits="userSpaceOnUse" x="-10" y="-10" width="120" height="120"><rect x="-10" y="-10" width="120" height="120" fill="#fff"/>${cut(12.5, 12.5)}${cut(-3.5, 28.5)}</mask>` +
    `<mask id="mdw1" maskUnits="userSpaceOnUse" x="-10" y="-10" width="120" height="120"><rect x="-10" y="-10" width="120" height="120" fill="#fff"/>${cut(-3.5, 28.5)}</mask></defs>` +
    `<rect x="32" y="0" width="68" height="68" rx="16.32" fill="#607A30" mask="url(#mdw0)"/>` +
    `<rect x="16" y="16" width="68" height="68" rx="16.32" fill="#EFC04A" mask="url(#mdw1)"/>` +
    `<rect x="0" y="32" width="68" height="68" rx="16.32" fill="#A8561C"/>` +
    `<rect x="31.28" y="32" width="5.44" height="68" fill="#F7F1E8"/><rect x="0" y="63.28" width="68" height="5.44" fill="#F7F1E8"/>` +
    `</svg>`
  );
}

export interface ChromeCallbacks {
  onReplay(): void;
  /** The bar's back / forward arrows (also what ArrowLeft / ArrowRight do). */
  onPrev(): void;
  onNext(): void;
  onCta(href: string): void;
  /** The Watch / Pause control, and the intro's Watch button. */
  onPlayToggle(): void;
  /** The mute/unmute control — only ever wired up when the demo has watch-mode
   *  audio to play at all (see the `sound` field below). */
  onSoundToggle(): void;
  /** The volume slider moved (0–1). Only wired when the sound control is. */
  onVolume(v: number): void;
}

/** Touch: holding the sound control this long opens the volume slider
 *  instead of toggling mute. */
const HOLD_MS = 450;
/** A touch-opened slider closes itself after this long untouched. */
const VOL_IDLE_MS = 2500;

/** The card's own fade-out (`.mado-card` opacity transition in styles.ts), plus a frame: the
 *  step's instruction waits this long after the card goes, so the two are never on screen together. */
const CHAPTER_FADE_MS = 260;

const CHEVRON_L =
  '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHEVRON_R =
  '<svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const ARROW_R =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
const REPLAY_ICON =
  '<svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true" focusable="false"><path d="M4.2 10a5.8 5.8 0 1 0 1.9-4.3M4 3.5v3.3h3.3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';

const PLAY_ICON =
  '<svg class="mado-play-i" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">' +
  '<path d="M6 3.6v12.8c0 .8.9 1.3 1.6.9l10-6.4c.6-.4.6-1.3 0-1.7l-10-6.4C6.9 2.3 6 2.8 6 3.6z"/></svg>';
const PAUSE_ICON =
  '<svg class="mado-pause-i" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">' +
  '<rect x="4.5" y="3.5" width="4" height="13" rx="1.2"/><rect x="11.5" y="3.5" width="4" height="13" rx="1.2"/></svg>';
/** Speaker + sound waves — the "on" state of the mute/unmute control. */
const SOUND_ON_ICON =
  '<svg class="mado-sound-on" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<path d="M3.5 7.5v5h2.7l4.3 3.6V3.9L6.2 7.5H3.5z" fill="currentColor"/>' +
  '<path d="M13.2 7c1.1.7 1.8 1.9 1.8 3.3s-.7 2.6-1.8 3.3" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/></svg>';
/** Speaker + an X — the "muted" state. */
const SOUND_OFF_ICON =
  '<svg class="mado-sound-off" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">' +
  '<path d="M3.5 7.5v5h2.7l4.3 3.6V3.9L6.2 7.5H3.5z" fill="currentColor"/>' +
  '<path d="M13 7.6l3.6 3.6M16.6 7.6L13 11.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';

export class Chrome {
  readonly root: HTMLElement;
  readonly replay: HTMLElement;
  private readonly bar: HTMLElement;
  private readonly count: HTMLElement;
  private readonly prev: HTMLButtonElement;
  private readonly play: HTMLButtonElement;
  /** Mute/unmute — only built when the demo has any watch-mode audio at all
   *  (`theme.audio.narration || .music || .sfx`); absent otherwise, so a
   *  demo with no narrated/musical content shows no sound affordance. */
  private readonly sound: HTMLButtonElement | null = null;
  private range: HTMLInputElement | null = null;
  private cta: HTMLAnchorElement | null = null;
  /** The intro / chapter card layer; the runtime mounts it in the viewport. */
  readonly card: HTMLElement;
  private cardTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly logoUrl: string | null;
  /** The current step has a target under the bar (`setYield`). */
  private overlap = false;
  /** A narration ring is lit under the bar (`setCueYield`). */
  private cueOverlap = false;
  /** The bar sits under the device frame (runtime.ts `dock`): nothing is under it to step aside for. */
  private docked = false;
  private ended = false;
  /** What a tap anywhere on the intro cover does; set while the cover is up. */
  private introStart: (() => void) | null = null;
  private readonly s: Strings;

  constructor(
    private readonly doc: DemoDoc,
    cb: ChromeCallbacks,
    assetBase?: string | null,
  ) {
    this.s = stringsFor(doc.locale);
    const logo = doc.theme.logo ? doc.assets[doc.theme.logo] : undefined;
    this.logoUrl = logo
      ? bestUrl(logo, 96, typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, assetBase)
      : null;

    this.root = el('div', 'mado-chrome');

    // Watch / Pause. First in the bar, where a video's play control sits, so
    // the one affordance that turns a click-through into something that plays
    // itself is found without being explained.
    this.play = document.createElement('button');
    this.play.type = 'button';
    this.play.className = 'mado-play';
    this.play.innerHTML = PLAY_ICON + PAUSE_ICON;
    this.play.addEventListener('click', () => cb.onPlayToggle());
    this.setPlaying(false);
    this.root.appendChild(this.play);

    // Back / forward. After Watch, before the progress bar: the arrows a
    // viewer reaches for without being told, in every demo and every embed.
    const arrow = (cls: string, label: string, icon: string, on: () => void) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `mado-nav ${cls}`;
      b.setAttribute('aria-label', label);
      b.innerHTML = icon;
      b.addEventListener('click', on);
      return b;
    };
    this.prev = arrow('mado-prev', this.s.prevStep, CHEVRON_L, () => cb.onPrev());
    this.root.append(
      this.prev,
      arrow('mado-next', this.s.nextStep, CHEVRON_R, () => cb.onNext()),
    );
    this.setBackable(false);

    if (doc.theme.audio?.narration || doc.theme.audio?.music || doc.theme.audio?.sfx) {
      const sound = document.createElement('button');
      sound.type = 'button';
      sound.className = 'mado-sound';
      sound.innerHTML = SOUND_ON_ICON + SOUND_OFF_ICON;
      sound.classList.add('mado-sound--muted');
      sound.setAttribute('aria-label', this.s.unmute);
      this.sound = sound;
      this.root.appendChild(this.buildVolume(sound, cb));
    }

    if (this.logoUrl) this.root.appendChild(this.logoImg('mado-logo'));

    const progress = el('div', 'mado-progress');
    this.bar = el('i');
    progress.appendChild(this.bar);

    this.count = el('span', 'mado-count');

    this.root.append(progress, this.count);

    if (doc.theme.badge) this.root.appendChild(this.buildBadge());

    // The end card: the creator's logo, a heading, their call to action if
    // they set one, and a way to watch again. `mado-replay` stays the layer's
    // class — embedders' tests and the player's own reach for it.
    this.replay = el('div', 'mado-replay');
    const end = el('div', 'mado-end');
    // Each child gets its own place in the entrance (--i), so the card
    // arrives as a sequence — mark, words, actions — rather than all at once.
    const part = (node: HTMLElement, i: number) => {
      node.classList.add('mado-end-part');
      node.style.setProperty('--i', String(i));
      end.appendChild(node);
    };
    if (this.logoUrl) part(this.logoImg('mado-end-logo'), 0);
    else {
      const ok = el('div', 'mado-end-check');
      ok.innerHTML = CHECK_ICON;
      part(ok, 0);
    }
    const kicker = el('p', 'mado-end-k');
    kicker.textContent = this.s.done(doc.steps.length);
    part(kicker, 1);
    const h = el('p', 'mado-end-h');
    h.textContent = doc.cta?.headline || doc.title;
    part(h, 2);
    const actions = el('div', 'mado-end-actions');
    if (doc.cta) {
      const a = document.createElement('a');
      a.className = 'mado-end-cta';
      a.href = doc.cta.href;
      a.target = '_blank';
      a.rel = 'noopener';
      a.innerHTML = `<span></span>${ARROW_R}`;
      a.firstElementChild!.textContent = doc.cta.label;
      a.addEventListener('click', () => cb.onCta(doc.cta!.href));
      actions.appendChild(a);
    }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = doc.cta ? 'mado-end-again mado-end-again--quiet' : 'mado-end-again';
    btn.innerHTML = `${REPLAY_ICON}<span></span>`;
    btn.lastElementChild!.textContent = this.s.replay;
    btn.addEventListener('click', () => cb.onReplay());
    actions.appendChild(btn);
    part(actions, 3);
    this.replay.appendChild(end);

    this.card = el('div', 'mado-card');
    // The whole cover is the invitation, not the one button: on a phone a
    // viewer taps the screen they were shown and expects it to respond
    // (real-iPhone report: "tapped on the slides, nothing happened"). Start
    // keeps the focus and the keyboard; Watch and Start handle themselves.
    this.card.addEventListener('click', (e) => {
      const start = this.introStart;
      if (!start || !this.introOpen) return;
      if (e.target instanceof Element && e.target.closest('button, a')) return;
      this.hideCard();
      start();
    });
  }

  private logoImg(cls: string): HTMLImageElement {
    const img = document.createElement('img');
    img.className = cls;
    img.src = this.logoUrl!;
    img.alt = '';
    img.decoding = 'async';
    // The cover and end card are paper; the demo's logo is usually made for its own dark
    // background and the bar (a light wordmark), and vanished on the paper (2026-11-03 review).
    if (cls === 'mado-end-logo') {
      const check = (): void => {
        if (faintLogo(img, this.doc.theme.background)) {
          img.classList.add('mado-end-logo--chip');
          // The demo's own background when it is dark (the logo was made for it), else charcoal.
          const bg = this.doc.theme.background;
          img.style.background = (luminance(bg) ?? 1) < 0.18 ? bg : '#22201C';
        }
      };
      if (img.complete && img.naturalWidth) check();
      else img.addEventListener('load', check, { once: true });
    }
    return img;
  }

  /**
   * The intro: the demo's first chapter as a cover, over the first frame, until
   * the viewer starts. Only shown when the creator put a chapter on the start
   * step — an embed without one opens straight onto the interactive frame.
   */
  showIntro(
    title: string,
    onStart: () => void,
    onWatch: () => void,
    /** `theme.cover: 'cta'`: the call to action rides on the cover as a link button. */
    onCta?: (href: string) => void,
  ): void {
    this.clearCard();
    const box = el('div', 'mado-card-box');
    if (this.logoUrl) box.appendChild(this.logoImg('mado-end-logo'));
    const t = el('p', 'mado-card-t');
    t.textContent = title;
    const sub = el('p', 'mado-card-sub');
    sub.textContent = this.doc.title;
    const go = document.createElement('button');
    go.type = 'button';
    go.className = 'mado-end-again';
    // Says what it is: the viewer clicks through the steps themselves (Watch plays it).
    go.textContent = this.s.clickThrough;
    go.addEventListener('click', () => {
      this.hideCard();
      onStart();
    });
    // The second way in: let it play. Quiet next to Start — tapping through
    // is the product; watching is the offer for someone who would rather not.
    const watch = document.createElement('button');
    watch.type = 'button';
    watch.className = 'mado-end-again mado-end-again--quiet mado-card-watch';
    watch.innerHTML = `${PLAY_ICON}<span></span>`;
    watch.lastElementChild!.textContent = this.s.watch;
    watch.addEventListener('click', () => {
      this.hideCard();
      onWatch();
    });
    const actions = el('div', 'mado-card-actions');
    actions.append(go, watch);
    const cta = this.doc.cta;
    if (onCta && cta) {
      const a = document.createElement('a');
      // Quiet, like Watch: Start stays the one filled button on the cover.
      a.className = 'mado-end-again mado-end-again--quiet mado-card-cta';
      a.href = cta.href;
      a.target = '_blank';
      a.rel = 'noopener';
      a.innerHTML = `<span></span>${ARROW_R}`;
      a.firstElementChild!.textContent = cta.label;
      a.addEventListener('click', () => onCta(cta.href));
      actions.appendChild(a);
    }
    box.append(t);
    if (title !== this.doc.title) box.append(sub);
    box.append(actions);
    this.card.appendChild(box);
    this.card.classList.add('mado-card--in', 'mado-card--intro');
    // The first step's instruction waits under the cover, as it does under a chapter card: it
    // peeked out under the cover, or sat on top of its scrim (2026-11-03 review).
    this.card.parentElement?.classList.add('mado-viewport--chapter');
    this.introStart = onStart;
    this.applyYield();
  }

  /**
   * The step's target is under the bar. Phone apps keep their primary
   * controls at the bottom — the tab bar, a full-width button — exactly
   * where the chrome sits, and a bar over the target both dims it and takes
   * the tap. So the whole bar steps aside for that step: opacity only, and
   * nothing in it is interactive or focusable, until a step whose target is
   * clear of it brings it back. Hiding only the control the target touches
   * would leave the gradient dimming the target and a bar with a hole in it.
   *
   * Not while the intro cover is up — the cover owns input, so the bar can
   * show what it offers — and not on the end card, where the badge and the
   * count belong and the scrim covers the target anyway.
   */
  setYield(overlap: boolean): void {
    this.overlap = overlap;
    this.applyYield();
  }

  /**
   * How far up from the bottom the bar reaches, as laid out (opacity does not change it). Its
   * controls are 44 px tap targets and the badge can wrap in a narrow player, so it is often
   * taller than `CHROME_CLEAR_PX`: a target whose centre sits in that extra band was covered by
   * the Next button and the bar never stepped aside (e2e "tap advances", desktop, 2026-10-22).
   */
  height(): number {
    return this.root.offsetHeight;
  }

  /** A lit narration ring reaches under the bar: it steps aside until the ring goes. */
  setCueYield(overlap: boolean): void {
    if (this.cueOverlap === overlap) return;
    this.cueOverlap = overlap;
    this.applyYield();
  }

  /** The bar sits under the device frame, off the screen (runtime.ts `dock`). */
  setDocked(on: boolean): void {
    this.docked = on;
    this.applyYield();
  }

  private applyYield(): void {
    const yields =
      (this.overlap || this.cueOverlap) && !this.ended && !this.introOpen && !this.docked;
    this.root.classList.toggle('mado-chrome--yield', yields);
    // `pointer-events` in the stylesheet covers taps; `inert` keeps the Tab
    // key off an invisible Watch button (ignored where unsupported).
    this.root.toggleAttribute('inert', yields);
  }

  /**
   * Reflect watch mode on the control: icon and name. The name changes, so
   * it is not also a pressed toggle — a control that says "Pause" and is
   * "pressed" reads as two states to a screen reader.
   */
  setPlaying(on: boolean): void {
    this.play.classList.toggle('mado-play--on', on);
    this.play.setAttribute('aria-label', on ? this.s.pause : this.s.watch);
  }

  /**
   * The sound control plus its volume slider. Mouse: the slider appears
   * above the button on hover or keyboard focus. Touch: a tap still mutes;
   * pressing and holding opens the slider (closed again by a tap elsewhere or
   * after a few idle seconds). Only opacity/transform ever change.
   */
  private buildVolume(sound: HTMLButtonElement, cb: ChromeCallbacks): HTMLElement {
    const wrap = el('div', 'mado-vol');
    const pop = el('div', 'mado-vol-pop');
    const range = document.createElement('input');
    range.type = 'range';
    range.className = 'mado-vol-range';
    range.min = '0';
    range.max = '100';
    range.step = '5';
    range.value = '100';
    range.setAttribute('aria-label', this.s.volume);
    this.range = range;
    pop.appendChild(range);
    wrap.append(sound, pop);

    let hold: ReturnType<typeof setTimeout> | null = null;
    let idle: ReturnType<typeof setTimeout> | null = null;
    let held = false;
    let touch = false;
    const outside = (e: Event): void => {
      if (!(e.target instanceof Node && wrap.contains(e.target))) close();
    };
    const close = (): void => {
      wrap.classList.remove('mado-vol--open');
      document.removeEventListener('pointerdown', outside, true);
      if (idle) clearTimeout(idle);
      idle = null;
    };
    const bump = (): void => {
      if (idle) clearTimeout(idle);
      idle = wrap.classList.contains('mado-vol--open') ? setTimeout(close, VOL_IDLE_MS) : null;
    };
    const open = (): void => {
      held = true;
      wrap.classList.add('mado-vol--open');
      document.addEventListener('pointerdown', outside, true);
      bump();
    };

    sound.addEventListener('pointerdown', (e) => {
      held = false;
      touch = e.pointerType !== 'mouse';
      if (e.pointerType === 'mouse') return;
      if (hold) clearTimeout(hold);
      hold = setTimeout(open, HOLD_MS);
    });
    const cancelHold = (): void => {
      if (hold) clearTimeout(hold);
      hold = null;
    };
    sound.addEventListener('pointerup', cancelHold);
    sound.addEventListener('pointercancel', cancelHold);
    sound.addEventListener('contextmenu', (e) => e.preventDefault());
    sound.addEventListener('click', () => {
      // The release of a press-and-hold opened the slider; it is not a mute.
      if (held) {
        held = false;
        return;
      }
      cb.onSoundToggle();
      // Touch has no hover, so the slider would stay hidden behind a
      // press-and-hold nobody tries: unmuting shows it for a moment.
      if (touch && !sound.classList.contains('mado-sound--muted')) open();
    });
    range.addEventListener('input', () => {
      cb.onVolume(Number(range.value) / 100);
      bump();
    });
    // The slider is not the stage: a drag along it is not a swipe, a wheel
    // over it does not scroll the step.
    for (const t of ['pointerdown', 'pointermove', 'pointerup', 'wheel'] as const) {
      pop.addEventListener(t, (e) => e.stopPropagation(), { passive: true });
    }
    return wrap;
  }

  /** Reflect the viewer's volume (0–1) on the slider. */
  setVolume(v: number): void {
    if (this.range) this.range.value = String(Math.round(v * 100));
  }

  /** Reflect the current mute state on the sound control. `on` = unmuted
   *  (sound audible). No-op when this demo has no watch-mode audio at all. */
  setSoundOn(on: boolean): void {
    if (!this.sound) return;
    this.sound.classList.toggle('mado-sound--muted', !on);
    this.sound.setAttribute('aria-label', on ? this.s.mute : this.s.unmute);
  }

  /** A chapter card is up and has not been dismissed. */
  get cardOpen(): boolean {
    return this.card.classList.contains('mado-card--in');
  }

  /** A chapter's title, briefly, as the viewer walks into it. Never blocks input. */
  flashChapter(title: string, index: number): void {
    this.clearCard();
    const box = el('div', 'mado-card-box');
    const k = el('p', 'mado-card-k');
    k.textContent = this.s.part(index + 1);
    const t = el('p', 'mado-card-t');
    t.textContent = title;
    box.append(k, t);
    this.card.appendChild(box);
    this.card.classList.add('mado-card--in');
    // The step's instruction waits under the card and fades in as it goes (styles.ts).
    this.card.parentElement?.classList.add('mado-viewport--chapter');
    this.cardTimer = setTimeout(() => this.hideCard(), CHAPTER_MS);
  }

  hideCard(): void {
    const viewport = this.card.parentElement;
    // The instruction under a chapter card stays hidden until the card has faded out (2026-10-16:
    // "the chapter card's text is still visible, faded, behind the first callout card").
    if (
      viewport?.classList.contains('mado-viewport--chapter') &&
      this.card.classList.contains('mado-card--in')
    ) {
      if (this.fadeTimer) clearTimeout(this.fadeTimer);
      this.fadeTimer = setTimeout(() => {
        this.fadeTimer = null;
        if (!this.cardOpen) viewport.classList.remove('mado-viewport--chapter');
      }, CHAPTER_FADE_MS);
    } else viewport?.classList.remove('mado-viewport--chapter');
    this.card.classList.remove('mado-card--in', 'mado-card--intro');
    this.introStart = null;
    this.applyYield();
  }

  get introOpen(): boolean {
    return this.card.classList.contains('mado-card--intro');
  }

  private clearCard(): void {
    if (this.cardTimer) clearTimeout(this.cardTimer);
    this.cardTimer = null;
    if (this.fadeTimer) clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
    this.card.replaceChildren();
    this.card.parentElement?.classList.remove('mado-viewport--chapter');
    this.card.classList.remove('mado-card--intro');
  }

  private buildBadge(): HTMLElement {
    const a = document.createElement('a');
    a.className = 'mado-badge';
    a.href = `${BADGE_HOME}/from/${encodeURIComponent(this.doc.id)}?utm_source=embed&utm_medium=badge`;
    a.target = '_blank';
    a.rel = 'noopener';
    a.innerHTML = `${markSvg()}<span></span>`;
    a.lastElementChild!.textContent = this.s.madeWith;
    return a;
  }

  /** Progress uses scaleX, never width — width would relayout on every step. */
  setProgress(index: number, total: number): void {
    this.bar.style.transform = `scaleX(${total <= 1 ? 1 : (index + 1) / total})`;
    this.count.textContent = `${index + 1} / ${total}`;
  }

  /** Back is only there when there is somewhere to go back to. */
  setBackable(can: boolean): void {
    this.prev.disabled = !can;
  }

  showCta(onCta: (href: string) => void): void {
    if (this.cta || !this.doc.cta) return;
    const a = document.createElement('a');
    a.className = this.doc.cta.style === 'secondary' ? 'mado-cta mado-cta--quiet' : 'mado-cta';
    a.href = this.doc.cta.href;
    a.target = '_blank';
    a.rel = 'noopener';
    a.textContent = this.doc.cta.label;
    a.addEventListener('click', () => onCta(this.doc.cta!.href));
    this.cta = a;
    // On the screen, with the cards: a docked bar's parent is the whole player, under the device.
    (this.docked ? this.card.parentElement : this.root.parentElement)?.appendChild(a);
  }

  /**
   * Keep the CTA off the step's instruction: where the boxes (viewport px) cross it, it moves to
   * sit just above the topmost one, or to the top edge when there is no room above.
   */
  avoidCta(boxes: Array<Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>>): void {
    const cta = this.cta;
    const host = cta?.parentElement;
    if (!cta || !host) return;
    cta.style.bottom = '';
    cta.style.top = '';
    const hit = (): Array<Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>> => {
      const r = cta.getBoundingClientRect();
      return boxes.filter(
        (b) => b.left < r.right && b.right > r.left && b.top < r.bottom && b.bottom > r.top,
      );
    };
    const crossing = hit();
    if (!crossing.length) return;
    const h = host.getBoundingClientRect();
    const top = Math.min(...crossing.map((b) => b.top));
    cta.style.bottom = `${Math.round(h.bottom - top + 8)}px`;
    const r = cta.getBoundingClientRect();
    if (r.top < h.top + 8 || hit().length) {
      cta.style.bottom = 'auto';
      cta.style.top = '16px';
    }
  }

  hideCta(): void {
    this.cta?.remove();
    this.cta = null;
  }

  setEnded(ended: boolean): void {
    this.ended = ended;
    this.replay.classList.toggle('mado-replay--in', ended);
    this.applyYield();
  }
}

/** WCAG luminance of 0-255 channels. */
function lum(r: number, g: number, b: number): number {
  const c = (v: number): number => {
    const x = v / 255;
    return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * c(r) + 0.7152 * c(g) + 0.0722 * c(b);
}

/**
 * Would the logo read badly on the card it sits on: most of its ink (opaque pixels) under 3:1
 * against the card's own colour. Measured on a 48 px copy. A logo with no transparent pixels
 * carries its own background and is left alone. Where the picture cannot be read (another
 * origin, a sandboxed embed), a dark demo background stands in: logos are made for it.
 */
export function faintLogo(img: HTMLImageElement, background: string): boolean {
  const card = img.parentElement ? getComputedStyle(img.parentElement).backgroundColor : '';
  // A see-through card (the phone story's cover) stands on the demo's own background.
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/.exec(card);
  const paper =
    m && (m[4] === undefined || +m[4] > 0.5)
      ? lum(+m[1]!, +m[2]!, +m[3]!)
      : m
        ? (luminance(background) ?? 1)
        : lum(255, 250, 242);
  const fallback = (luminance(background) ?? 1) < 0.18 && paper > 0.5;
  try {
    const w = 48;
    const h = Math.max(1, Math.round((w * img.naturalHeight) / Math.max(1, img.naturalWidth)));
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d');
    if (!g) return fallback;
    g.drawImage(img, 0, 0, w, h);
    const d = g.getImageData(0, 0, w, h).data;
    let ink = 0;
    let faint = 0;
    let clear = 0;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3]! < 128) {
        clear++;
        continue;
      }
      ink++;
      const l = lum(d[i]!, d[i + 1]!, d[i + 2]!);
      if ((Math.max(l, paper) + 0.05) / (Math.min(l, paper) + 0.05) < 3) faint++;
    }
    return !!clear && !!ink && faint / ink > 0.35;
  } catch {
    return fallback;
  }
}
